import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { FileText, Calendar, RefreshCw, AlertTriangle, Clock, CheckCircle, Loader2, Search, ArrowUpDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const BUCKETS = [
  { id: 'all', label: 'All Active', color: '#1F2937' },
  { id: 'expired', label: 'Expired', color: '#DC2626' },
  { id: 'expiring_30', label: '< 30 days', color: '#DC2626' },
  { id: 'expiring_90', label: '30-90 days', color: '#D97706' },
  { id: 'active', label: '> 90 days', color: '#059669' },
];

const bucketBadge = (days) => {
  if (days == null) return { text: 'No end date', bg: '#F3F4F6', color: '#6B7280' };
  if (days < 0) return { text: `Expired ${Math.abs(days)}d ago`, bg: '#FEE2E2', color: '#991B1B' };
  if (days <= 30) return { text: `${days}d left`, bg: '#FEE2E2', color: '#991B1B' };
  if (days <= 90) return { text: `${days}d left`, bg: '#FEF3C7', color: '#92400E' };
  return { text: `${days}d left`, bg: '#D1FAE5', color: '#065F46' };
};

export const ActiveContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState('all');
  const [search, setSearch] = useState('');
  const [editingContract, setEditingContract] = useState(null);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ax.get('/api/admin/contracts/active');
      setContracts(data.contracts || []);
      setCounts(data.counts || {});
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const startRenewal = async (dealId) => {
    if (!window.confirm('Create a new renewal deal for this customer?')) return;
    try {
      await ax.post('/api/admin/contracts/start-renewal', { deal_id: dealId });
      alert('Renewal deal created — check your pipeline');
      fetchContracts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start renewal');
    }
  };

  const filtered = contracts
    .filter(c => bucket === 'all' || c.renewal_bucket === bucket)
    .filter(c => !search || [c.name, c.email, c.company, c.state].filter(Boolean).some(v => v.toLowerCase().includes(search.toLowerCase())));

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div data-testid="active-contracts">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={22} style={{ color: 'var(--teal)' }} />
            Active Contracts
          </h2>
          <p style={{ fontSize: '0.813rem', color: '#9CA3AF', margin: 0 }}>
            {contracts.length} signed contracts • Sorted by expiration date
          </p>
        </div>
        <Button onClick={fetchContracts} className="btn-secondary" data-testid="contracts-refresh">
          <RefreshCw size={14} style={{ marginRight: 6 }} /> Refresh
        </Button>
      </div>

      {/* Bucket Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {BUCKETS.map(b => (
          <button
            key={b.id}
            onClick={() => setBucket(b.id)}
            data-testid={`bucket-${b.id}`}
            style={{
              padding: '0.5rem 0.875rem', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: '0.813rem', fontWeight: 600,
              background: bucket === b.id ? b.color : '#F3F4F6',
              color: bucket === b.id ? '#fff' : '#374151',
            }}
          >
            {b.label}
            {b.id !== 'all' && counts[b.id] != null && (
              <span style={{ marginLeft: 6, padding: '1px 6px', background: bucket === b.id ? 'rgba(255,255,255,0.25)' : '#E5E7EB', borderRadius: 100, fontSize: '0.7rem' }}>
                {counts[b.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', maxWidth: 320, marginBottom: '1rem' }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, company, state..." style={{ paddingLeft: 32, fontSize: '0.813rem', height: 36 }} data-testid="contracts-search" />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader2 size={28} className="animate-spin" style={{ color: 'var(--teal)' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB' }}>
          <FileText size={36} style={{ color: '#9CA3AF', margin: '0 auto 0.75rem' }} />
          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>No contracts found</div>
          <div style={{ fontSize: '0.813rem', color: '#6B7280', marginTop: 4 }}>
            Closed Won deals will appear here as signed contracts.
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={th}>Customer</th>
                <th style={th}>Company</th>
                <th style={th}>State</th>
                <th style={th}>Energy</th>
                <th style={th}>Start</th>
                <th style={th}><span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>End <ArrowUpDown size={11} /></span></th>
                <th style={th}>Status</th>
                <th style={th}>Assigned</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const badge = bucketBadge(c.days_remaining);
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`contract-row-${c.id}`}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{c.email}</div>
                    </td>
                    <td style={td}>{c.company || '—'}</td>
                    <td style={td}>{c.state || '—'}</td>
                    <td style={td}>{c.energy_type || '—'}</td>
                    <td style={td}>{fmtDate(c.contract_start_date)}</td>
                    <td style={td}>{fmtDate(c.contract_end_date)}</td>
                    <td style={td}>
                      <span style={{ background: badge.bg, color: badge.color, padding: '3px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600 }}>
                        {badge.text}
                      </span>
                    </td>
                    <td style={td}>{c.assigned_to_name || <span style={{ color: '#9CA3AF' }}>Unassigned</span>}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button
                        onClick={() => setEditingContract(c)}
                        style={btn}
                        data-testid={`contract-edit-${c.id}`}
                      >
                        <Calendar size={13} />
                      </button>
                      <button
                        onClick={() => startRenewal(c.id)}
                        style={{ ...btn, background: 'var(--teal)', color: '#fff', marginLeft: 4 }}
                        data-testid={`contract-renew-${c.id}`}
                      >
                        Renew
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingContract && (
        <ContractDatesModal
          contract={editingContract}
          onClose={() => setEditingContract(null)}
          onSaved={() => { setEditingContract(null); fetchContracts(); }}
        />
      )}
    </div>
  );
};

const th = { padding: '0.625rem 0.75rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '0.75rem', fontSize: '0.875rem', color: 'var(--navy)' };
const btn = { padding: '4px 10px', borderRadius: 4, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500 };

const ContractDatesModal = ({ contract, onClose, onSaved }) => {
  const [startDate, setStartDate] = useState(contract.contract_start_date?.slice(0, 10) || '');
  const [endDate, setEndDate] = useState(contract.contract_end_date?.slice(0, 10) || '');
  const [length, setLength] = useState(contract.contract_length_months || 12);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (startDate) payload.contract_start_date = new Date(startDate).toISOString();
      if (endDate) payload.contract_end_date = new Date(endDate).toISOString();
      if (length) payload.contract_length_months = parseInt(length);
      await ax.put(`/api/admin/deals/${contract.id}/contract-dates`, payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, padding: '1.5rem' }} data-testid="contract-dates-modal">
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', marginBottom: '0.25rem' }}>Contract Dates</h3>
        <p style={{ fontSize: '0.813rem', color: '#6B7280', marginBottom: '1.25rem' }}>{contract.name} • {contract.company}</p>

        <div style={{ display: 'grid', gap: '0.875rem' }}>
          <label>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Start Date</div>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: 6 }} data-testid="contract-start-date" />
          </label>
          <label>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>End Date</div>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: 6 }} data-testid="contract-end-date" />
          </label>
          <label>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Length (months)</div>
            <input type="number" value={length} onChange={(e) => setLength(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: 6 }} data-testid="contract-length" />
            <div style={{ fontSize: '0.7rem', color: '#9CA3AF', marginTop: 4 }}>If start date is set, end date will auto-calculate from length.</div>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
          <Button onClick={onClose} className="btn-secondary">Cancel</Button>
          <Button onClick={save} className="btn-primary" disabled={saving} data-testid="contract-dates-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            <span style={{ marginLeft: 6 }}>Save</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
